import { isMobile } from '@common-util/device-helper';
import { scaleImagePreserveAspect } from '@common-util/texture-util';
import { FileTextureLayer } from '@models/classes';
import { Kb3dService } from '@models/classes/decorators/service-decorator';
import { eMaterialLocation, eTextureChannel } from '@models/classes/enums';
import { IAdvancedMaterialNode, IFilter, IMaterial, ISearchArgs, ITextureLayer } from '@models/classes/kb3d-models';
import { AdvancedMaterialNode } from '@models/classes/materials/advanced-material-node';
import { Token } from '@models/classes/token';
import { isAbsoluteUrl } from '@models/classes/utilities';
import { LOCAL_ENVIRONMENT_DOMAIN } from '@models/constants';
import { BehaviorSubject, Observable, Subject, combineLatest, from, of } from 'rxjs';
import { map, shareReplay, switchMap, take } from 'rxjs/operators';
import { ABORTED, HttpService, IBinaryResponse } from './http-service';
import { attachLoadingTimer } from './loading-timer-helper';

const LIBRARY_PATH = '%LIBRARY%';

// If this is a raster image, we store the bitmap. If this is an SVG, then we store the blob so it can be resized efficiently
const textureCache = new Map<string, Observable<{ bitmap: ImageBitmap | Blob; url: string }>>();
const materialCache = new Map<string, Observable<IMaterial | undefined>>();

export interface IMaterialService {
    getMaterialById: (id: string) => Observable<IMaterial | undefined>;
    getMaterialLibraryCategories: () => IMaterialCategory[];
    getMaterialLibraryOptions: (category: string) => IMaterial[];
    getTexturePath: (node: FileTextureLayer, detailLevel?: number) => string;
    getTextureImage: (node: FileTextureLayer) => Observable<{ bitmap: ImageBitmap; url: string }>;
    getLibraryTexturePath: (path: string, quality?: number) => string;
}

export interface IMaterialCategory {
    id: string;
    label: string;
}

const TEXTURE_SIZES = [128, 256, 512, 1024, 2048, null];
const LIBRARY_TEXTURE_SIZES = [256, 256, 512, 1024, 2048, 2048];
const LIBRARY_ENV_SIZES = [128, 256, 512, 1024];
const LIBRARY_MOBILE_ENV_SIZES = [128, 128, 256, 512];
const MAX_DETAIL_LEVEL = 5;

@Kb3dService({
    token: Token.MaterialService,
})
export class MaterialService implements IMaterialService {
    public trackedAssetCalls = new Array<Promise<any>>();

    materialUpdateChannel = new Map<string, Subject<IMaterial | null>>();
    private abortSubjects = new Map<string, Subject<void>>();
    textureDetailLevel = MAX_DETAIL_LEVEL;

    constructor(
        protected httpService: HttpService,
        protected assetRoot?: string,
        protected forceFullSizeTextures = false
    ) {
        if (forceFullSizeTextures) {
            this.textureDetailLevel = -1;
        } else if (window.screen.width <= 1024 || window.screen.height <= 1024) {
            this.textureDetailLevel = 2;
        }
    }

    public getMaterialLibraryCategories(): IMaterialCategory[] {
        const categoryIds = Object.keys(materialDefinitionSet);

        return categoryIds.map(id => ({
            id,
            label: materialDefinitionSet[id].label,
        }));
    }

    public getMaterialLibraryOptions(category: string): IMaterial[] {
        return LibraryMaterialsByCategory.get(category) || [];
    }

    public getMaterialById(guid: string): Observable<IMaterial | undefined> {
        if (materialCache.has(guid)) {
            return materialCache.get(guid) as Observable<IMaterial | undefined>;
        } else {
            let obs: Observable<IMaterial | undefined>;
            const material = LibraryMaterials.get(guid);

            if (material) {
                //it's a library  material
                obs = of<IMaterial | undefined>(
                    material
                    //asapScheduler
                );
            } else {
                //maybe a global material
                obs = from(this.httpService.get<IMaterial>(`/api/materials/${guid}`));
            }
            const updateChannel = new BehaviorSubject<IMaterial | null>(null);
            this.materialUpdateChannel.set(guid, updateChannel);

            obs = combineLatest([obs, updateChannel])
                .pipe(
                    map(([material, updatedMaterial]) => {
                        if (!material) {
                            return undefined;
                        }
                        return { ...material, ...(updatedMaterial || {}) };
                    })
                )
                .pipe(shareReplay(1));

            materialCache.set(guid, obs);
            return obs;
        }
    }

    public getTexturePath(node: FileTextureLayer, detailLevel?: number) {
        let width: number | null = null;
        let height: number | null = null;
        if (!node.path) {
            return '';
        }

        if (detailLevel === undefined) {
            detailLevel = this.textureDetailLevel;
        }
        detailLevel = Math.min(MAX_DETAIL_LEVEL, detailLevel);

        if (node.path.startsWith(LIBRARY_PATH)) {
            return this.getLibraryTexturePath(node.path, detailLevel);
        } else if (!isAbsoluteUrl(node.path)) {
            const url = this.getAbsoluteTexturePath(node.path);
            if (detailLevel >= 0) {
                width = TEXTURE_SIZES[detailLevel];
            }
            if (detailLevel >= 0) {
                height = TEXTURE_SIZES[detailLevel];
            }

            const params: string[] = [];
            if (width != null) params.push(`width=${width}`);
            if (height != null) params.push(`height=${height}`);
            if (params.length > 0) {
                return url + '?' + params.join('&');
            } else {
                return url;
            }
        } else {
            return node.path;
        }
    }

    public getTextureImage(node: FileTextureLayer, trackAssetCall = true) {
        const initialUrl = this.getTexturePath(node, 0);
        const url = this.getTexturePath(node);

        let bitmapObs: Observable<{ bitmap: ImageBitmap | Blob; url: string }>;

        if (textureCache.has(url)) {
            bitmapObs = textureCache.get(url)!;
        } else {
            bitmapObs = new Observable<{ bitmap: ImageBitmap | Blob; url: string }>(subject => {
                const activeLinkAbort = this.abortSubjects.get(node._dynamicId);
                // if there already is an httpService call on this node. Abort the previous one
                if (activeLinkAbort) {
                    activeLinkAbort.next();
                }

                const abortTextureLoad = new Subject<void>();
                this.abortSubjects.set(node._dynamicId, abortTextureLoad);

                // Textures are absolute so we have to add the cache params before passing to http service
                // With this code library textures are always progressively loaded regardless of the scene setting because they do not have a root scene
                // revisit this if we want to change this behavior but it is non-trivial to do so
                if (this.forceFullSizeTextures || (node._rootScene && !node._rootScene.progressiveTextureLoad)) {
                    const cacheParamUrl =
                        this.assetRoot && url.startsWith(this.assetRoot) ? this.httpService.addCacheParams(url) : url;

                    attachLoadingTimer(
                        this.httpService.getBinary(cacheParamUrl, 'blob', false, true, abortTextureLoad)
                    ).then(
                        blobResult => {
                            // clear the abort once the full image is loaded
                            const activeLinkAbort = this.abortSubjects.get(node._dynamicId);
                            if (activeLinkAbort) {
                                activeLinkAbort.complete();
                                this.abortSubjects.delete(node._dynamicId);
                            }

                            processImageBlob(blobResult).then(result => {
                                if (result) {
                                    subject.next({ bitmap: result, url: cacheParamUrl });
                                    subject.complete();
                                }
                            });
                        },
                        err => {
                            if (err !== ABORTED) {
                                console.warn(`Could not load texture ${cacheParamUrl}: ` + err.toString());
                            }
                            subject.complete();
                        }
                    );
                } else {
                    const initialCacheParamUrl =
                        this.assetRoot && url.startsWith(this.assetRoot)
                            ? this.httpService.addCacheParams(initialUrl)
                            : initialUrl;

                    const loadStartTime = performance.now();
                    attachLoadingTimer(
                        this.httpService.getBinary(initialCacheParamUrl, 'blob', false, true, abortTextureLoad)
                    ).then(
                        initialBlobResult => {
                            processImageBlob(initialBlobResult).then(result => {
                                const loadTime = performance.now() - loadStartTime;
                                if (loadTime > 2000) {
                                    console.warn(`Texture ${initialCacheParamUrl} took ${loadTime}ms to load`);
                                }
                                if (result) {
                                    subject.next({ bitmap: result, url: initialCacheParamUrl });
                                }
                                const cacheParamUrl =
                                    this.assetRoot && url.startsWith(this.assetRoot)
                                        ? this.httpService.addCacheParams(url)
                                        : url;
                                this.httpService
                                    .defer(() =>
                                        this.httpService.getBinary(cacheParamUrl, 'blob', false, true, abortTextureLoad)
                                    )
                                    .then(
                                        blobResult => {
                                            // clear the abort once the full image is loaded
                                            const activeLinkAbort = this.abortSubjects.get(node._dynamicId);
                                            if (activeLinkAbort) {
                                                activeLinkAbort.complete();
                                                this.abortSubjects.delete(node._dynamicId);
                                            }

                                            processImageBlob(blobResult).then(result => {
                                                if (result) {
                                                    subject.next({ bitmap: result, url: cacheParamUrl });
                                                    subject.complete();
                                                }
                                            });
                                        },
                                        err => {
                                            if (err !== ABORTED) {
                                                console.warn(
                                                    `Could not load texture ${cacheParamUrl}: ` + err.toString()
                                                );
                                            }
                                            subject.complete();
                                        }
                                    );
                            });
                        },
                        err => {
                            if (err !== ABORTED) {
                                console.warn(`Could not load texture ${initialCacheParamUrl}: ` + err.toString());
                            }
                            subject.complete();
                        }
                    );
                }
            }).pipe(shareReplay(1));
        }

        function processImageBlob(blob: IBinaryResponse<Blob>): Promise<Blob | ImageBitmap | null> {
            if (blob && blob.data) {
                if (blob.data.type === 'image/svg+xml') {
                    return Promise.resolve(blob.data);
                } else {
                    if (blob.data.type.match(/image/)) {
                        const opts =
                            blob.width && blob.height
                                ? {
                                      resizeWidth: blob.width,
                                      resizeHeight: blob.height,
                                      resizeQuality: 'medium' as ResizeQuality,
                                  }
                                : undefined;
                        return createImageBitmap(blob.data, opts).then(bitmap => {
                            return Promise.resolve(bitmap);
                        });
                    } else {
                        console.warn('source file is not an image: ', url);
                    }
                }
            }
            return Promise.resolve(null);
        }

        textureCache.set(url, bitmapObs);

        const obs = bitmapObs.pipe(
            switchMap(res => {
                /** bitmap is returned as blob for SVGs and imageBitmap for raster images */
                if (res.bitmap instanceof Blob) {
                    const blobToConvert = res.bitmap;
                    return new Promise<{ bitmap: ImageBitmap; url: string }>((resolve, reject) => {
                        const imageElement = document.createElement('img');

                        const blobUrl = URL.createObjectURL(blobToConvert);

                        imageElement.onload = () => {
                            // Figure out what resolution to actually draw the SVG at to fit within the envelope
                            const scaled = scaleImagePreserveAspect(
                                node.width,
                                node.height,
                                imageElement.naturalWidth,
                                imageElement.naturalHeight
                            );

                            createImageBitmap(imageElement, {
                                resizeWidth: scaled.drawWidth,
                                resizeHeight: scaled.drawHeight,
                            }).then(bitmap => {
                                resolve({ bitmap: bitmap, url: res.url });
                            });
                        };
                        imageElement.onerror = e => {
                            reject(e);
                        };
                        imageElement.src = blobUrl;
                    });
                } else {
                    return of({ bitmap: res.bitmap, url: res.url });
                }
            })
        );

        if (trackAssetCall) {
            this.trackAssetCall(obs.pipe(take(1)).toPromise());
        }

        return obs;
    }

    public getLibraryTexturePath(relativePath: string, quality = 0) {
        const path = relativePath.substring(LIBRARY_PATH.length);
        let res: number;
        if (quality < 0) {
            res = LIBRARY_TEXTURE_SIZES[LIBRARY_TEXTURE_SIZES.length - 1];
        } else {
            res = LIBRARY_TEXTURE_SIZES[quality];
        }
        return `${this.assetRoot}/assets/textures/${path}_${res}.jpg`;
    }

    public getAbsoluteTexturePath(relativePath: string) {
        return `${this.assetRoot}/media/${relativePath}`; //it's a relative path to the media folder
    }

    public getAbsoluteEnvironmentPath(path: string, quality: 0 | 1 | 2 | 3) {
        if (path) {
            let url: string;
            if (path.startsWith(LOCAL_ENVIRONMENT_DOMAIN)) {
                const res = isMobile() ? LIBRARY_MOBILE_ENV_SIZES[quality] : LIBRARY_ENV_SIZES[quality];

                url = `${this.assetRoot}/assets/environments/${path.replace(LOCAL_ENVIRONMENT_DOMAIN, '')}_${res}.env`;
            } else {
                url = this.getAbsoluteTexturePath(path);
            }
            return this.httpService.addCacheParams(url);
        }
        return '';
    }

    public searchMaterialByName(name: string, loadArchivedMaterials: boolean) {
        const archivedFilter = { type: 'property', property: 'archived', values: [false] } as IFilter;
        const searchArgs = { query: name + '*', sortField: 'Name', filters: [], descending: false } as ISearchArgs;
        if (!loadArchivedMaterials) {
            searchArgs.filters?.push(archivedFilter);
        }
        let result = null;
        if (name) {
            result = this.httpService.post<IMaterial[]>('/api/materials/search', searchArgs);
        }
        return result;
    }

    public getMaterialNameList() {
        const archivedFilter = { type: 'property', property: 'archived', values: [false] } as IFilter;
        const searchArgs = { fields: ['Guid', 'Name'], take: 10000, filters: [archivedFilter] } as ISearchArgs;
        return this.httpService.post<IMaterial[]>('/api/materials/search', searchArgs);
    }

    protected setMaterialCache(guid: string, mat: IMaterial) {
        materialCache.set(guid, of(mat));
    }

    protected trackAssetCall(p: Promise<any>) {
        const trackedPromise = p.finally(() => {
            this.trackedAssetCalls.remove(trackedPromise);
        });
        this.trackedAssetCalls.push(trackedPromise);
    }
}

export interface IEnvironmentInfo {
    name: string;
    label: string;
    path: string;
    previewUrl: string;
}

export const LibraryMaterials = new Map<string, IMaterial>();
export const LibraryMaterialsByCategory = new Map<string, IMaterial[]>();
export const LibraryEnvironments = new Array<IEnvironmentInfo>();
export const LibraryEnvironmentsMap = new Map<string, IEnvironmentInfo>();

function makeLibraryMaterial(tag: string, index: number) {
    //pad the index string with zeros
    let indexStr = index + '';
    while (indexStr.length < 2) indexStr = '0' + indexStr;
    const iname = tag + indexStr;
    const baseUrl = `${tag}/${iname}`;

    return <IMaterial>{
        id: -1,
        name: iname,
        guid: iname,
        tags: [{ name: tag }],
        data: <IAdvancedMaterialNode>{
            $type: Token.AdvancedMaterialNode.key,
            name: iname,
            id: iname,
            location: eMaterialLocation.library,
            textures: [
                {
                    $type: 'FileTextureLayer',
                    channel: eTextureChannel.ambientOcclusion,
                    path: `${LIBRARY_PATH}${baseUrl}_ao`,
                } as ITextureLayer,
                {
                    $type: 'FileTextureLayer',
                    channel: eTextureChannel.albedo,
                    path: `${LIBRARY_PATH}${baseUrl}_col`,
                } as ITextureLayer,
                {
                    $type: 'FileTextureLayer',
                    channel: eTextureChannel.metallic,
                    path: `${LIBRARY_PATH}${baseUrl}_met`,
                } as ITextureLayer,
                {
                    $type: 'FileTextureLayer',
                    channel: eTextureChannel.normal,
                    path: `${LIBRARY_PATH}${baseUrl}_nrm`,
                } as ITextureLayer,
                {
                    $type: 'FileTextureLayer',
                    channel: eTextureChannel.roughness,
                    path: `${LIBRARY_PATH}${baseUrl}_rgh`,
                } as ITextureLayer,
                //{ $type: "FileTextureLayer", channel: eTextureChannel.displacement, path: `${LIBRARY_PATH}${baseUrl}_disp` },
            ],
        },
    };
}

interface IMaterialDefinitionSet {
    [tagName: string]: {
        label: string;
        count: number;
        omit?: Partial<{
            //the textures to omit for
            [T in eTextureChannel]: boolean | number[];
        }>;
        props?: Partial<AdvancedMaterialNode> & { [key: string]: any };
    };
}

function fillLibraryMaterialsMap() {
    for (const tag in materialDefinitionSet) {
        const entry = materialDefinitionSet[tag];
        for (let index = 1; index <= entry.count; index++) {
            const mat = makeLibraryMaterial(tag, index);
            const matData = mat.data as AdvancedMaterialNode;
            matData.roughness = 1;
            matData.metallic = 0;

            if (entry.omit) {
                const omit = entry.omit;
                matData.textures = matData.textures.filter(t => {
                    if (omit[t.channel]) {
                        if (omit[t.channel] === true) {
                            return false;
                        }
                        if (Array.isArray(omit[t.channel]) && (omit[t.channel] as number[]).some(i => i === index)) {
                            return false;
                        }
                    }
                    return true;
                });
            }
            // Metallic is multiplied against the texture, so if the material has a metallic texture then set metallic to 1.
            if (matData.textures.findIndex(t => t.channel === eTextureChannel.metallic) > -1) {
                matData.metallic = 1;
            }

            if (entry.props) {
                for (const p in entry.props) {
                    (mat.data as any)[p] = entry.props[p];
                }
            }
            LibraryMaterials.set(mat.guid as string, mat);

            let categoryList = LibraryMaterialsByCategory.get(tag);
            if (!categoryList) {
                categoryList = [];
            }
            categoryList.push(mat);
            LibraryMaterialsByCategory.set(tag, categoryList);
        }
    }
}

const materialDefinitionSet: IMaterialDefinitionSet = {
    asphalt: {
        label: 'Asphalt',
        count: 15,
        omit: {
            [eTextureChannel.metallic]: true,
            [eTextureChannel.ambientOcclusion]: [1, 2, 9, 10, 11, 12, 13, 14, 15],
        },
    },
    bark: {
        label: 'Bark',
        count: 6,
        omit: {
            [eTextureChannel.metallic]: true,
            [eTextureChannel.ambientOcclusion]: [1],
        },
    },
    bricks: {
        label: 'Bricks',
        count: 17,
        omit: {
            [eTextureChannel.metallic]: true,
            [eTextureChannel.ambientOcclusion]: [1, 2, 3, 4],
        },
    },
    chipboard: {
        label: 'Chipboard',
        count: 3,
        omit: {
            [eTextureChannel.metallic]: true,
        },
    },
    concrete: {
        label: 'Concrete',
        count: 17,
        omit: {
            [eTextureChannel.metallic]: true,
            [eTextureChannel.ambientOcclusion]: [1, 2, 12, 14, 15, 16, 17],
            [eTextureChannel.roughness]: [1],
        },
    },
    corrugatedsteel: {
        label: 'Corrugated Steel',
        count: 5,
    },
    diamondplate: {
        label: 'Diamond Plate',
        count: 4,
        omit: {
            [eTextureChannel.ambientOcclusion]: true,
            [eTextureChannel.albedo]: [4],
        },
    },
    fabric: {
        label: 'Fabric',
        count: 29,
        omit: {
            [eTextureChannel.ambientOcclusion]: true,
            [eTextureChannel.metallic]: true,
        },
    },
    gravel: {
        label: 'Gravel',
        count: 8,
        omit: {
            [eTextureChannel.metallic]: true,
            [eTextureChannel.ambientOcclusion]: [1, 2],
        },
    },
    ground: {
        label: 'Ground',
        count: 28,
        omit: {
            [eTextureChannel.metallic]: true,
            [eTextureChannel.ambientOcclusion]: [1, 2, 3, 17],
        },
    },
    ice: {
        label: 'Ice',
        count: 4,
        omit: {
            [eTextureChannel.metallic]: true,
            [eTextureChannel.ambientOcclusion]: true,
        },
    },
    leather: {
        label: 'Leather',
        count: 14,
        omit: {
            [eTextureChannel.metallic]: true,
            [eTextureChannel.ambientOcclusion]: [1, 2, 3, 4, 5, 6, 7, 9, 10, 11, 12, 13, 14],
        },
    },
    marble: {
        label: 'Marble',
        count: 5,
        omit: {
            [eTextureChannel.metallic]: true,
            [eTextureChannel.ambientOcclusion]: true,
        },
    },
    metal: {
        label: 'Metal',
        count: 27,
        omit: {
            [eTextureChannel.ambientOcclusion]: true,
        },
    },
    metalplates: {
        label: 'Metal Plate',
        count: 3,
        omit: {
            [eTextureChannel.ambientOcclusion]: true,
        },
    },
    paint: {
        label: 'Paint',
        count: 6,
        omit: {
            [eTextureChannel.metallic]: true,
        },
    },
    paintedmetal: {
        label: 'Painted Metal',
        count: 5,
        omit: {
            [eTextureChannel.ambientOcclusion]: true,
        },
    },
    paintedplaster: {
        label: 'Painted Plaster',
        count: 5,
        omit: {
            [eTextureChannel.ambientOcclusion]: true,
            [eTextureChannel.metallic]: true,
        },
    },
    pavingstones: {
        label: 'Paving Stones',
        count: 41,
        omit: {
            [eTextureChannel.metallic]: true,
            [eTextureChannel.ambientOcclusion]: [1, 3, 4],
        },
    },
    pipe: {
        label: 'Pipe',
        count: 4,
        omit: {
            [eTextureChannel.ambientOcclusion]: true,
        },
    },
    planks: {
        label: 'Planks',
        count: 9,
        omit: {
            [eTextureChannel.ambientOcclusion]: [1, 2],
        },
    },
    plastic: {
        label: 'Plastic',
        count: 5,
        omit: {
            [eTextureChannel.metallic]: true,
            [eTextureChannel.ambientOcclusion]: true,
        },
    },
    rock: {
        label: 'Rock',
        count: 14,
        omit: {
            [eTextureChannel.metallic]: true,
            [eTextureChannel.ambientOcclusion]: [1, 6, 7, 8, 9],
            [eTextureChannel.albedo]: [4],
        },
    },
    rocks: {
        label: 'Rocks',
        count: 6,
        omit: {
            [eTextureChannel.metallic]: true,
        },
    },
    roofingtiles: {
        label: 'Roofing Tiles',
        count: 5,
        omit: {
            [eTextureChannel.metallic]: true,
            [eTextureChannel.ambientOcclusion]: true,
        },
    },
    rust: {
        label: 'Rust',
        count: 5,
        omit: {
            [eTextureChannel.ambientOcclusion]: true,
        },
    },
    scratches: {
        label: 'Scratches',
        count: 5,
        omit: {
            [eTextureChannel.ambientOcclusion]: true,
            [eTextureChannel.metallic]: true,
            [eTextureChannel.albedo]: true,
        },
    },
    terrazzo: {
        label: 'Terrazzo',
        count: 8,
        omit: {
            [eTextureChannel.ambientOcclusion]: true,
            [eTextureChannel.metallic]: true,
        },
    },
    tiles: {
        label: 'Tiles',
        count: 37,
        omit: {
            [eTextureChannel.metallic]: true,
            [eTextureChannel.ambientOcclusion]: [
                1, 2, 3, 4, 5, 12, 13, 14, 15, 20, 21, 22, 23, 24, 25, 29, 30, 31, 32, 33, 34, 35, 36, 37,
            ],
        },
    },
    wood: {
        label: 'Wood',
        count: 20,
        omit: {
            [eTextureChannel.metallic]: true,
            [eTextureChannel.ambientOcclusion]: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 13, 14, 15, 16, 17, 18, 19, 20],
        },
    },
    woodfloor: {
        label: 'Wood Floor',
        count: 28,
        omit: {
            [eTextureChannel.metallic]: true,
            [eTextureChannel.ambientOcclusion]: [1, 2, 4, 5, 6, 8],
        },
    },
    woodsiding: {
        label: 'Wood Siding',
        count: 8,
        omit: {
            [eTextureChannel.metallic]: [5, 6, 7],
        },
    },
};

fillLibraryMaterialsMap();

/** create library environments */
for (const env of [
    // "country",
    // "environment",
    // "forest",
    // "night",
    // "parking",
    // "room",

    'adams_place_bridge',
    'aerodynamics_workshop',
    'autoshop',
    'ballroom',
    'boiler_room',
    'cape_hill',
    'colorful_studio',
    'fireplace',
    'greenwich_park',
    'hospital_room',
    'industrial_pipe_and_valve',
    'leadenhall_market',
    'machine_shop',
    'modern_buildings_night',
    'museum_of_ethnography',
    'mutianyu',
    'old_bus_depot',
    'photo_studio',
    'preller_drive',
    'red_wall',
    'rooitou_park',
    'royal_esplanade',
    'schadowplatz',
    'small_cathedral',
    'spruit_sunrise',
    'studio_small',
    'surgery',
    'venice_sunset',
    'winter_evening',
    'wooden_lounge',
]) {
    const label = env
        .split('_')
        .map(s => s.capitalize())
        .join(' ');
    const o = {
        name: env,
        label,
        path: `${LOCAL_ENVIRONMENT_DOMAIN}${env}`,
        previewUrl: `environments/${env}.jpg`,
    } as IEnvironmentInfo;
    LibraryEnvironments.push(o);
    LibraryEnvironmentsMap.set(o.path, o);
}
